/** C1 — "Record petition details": court, Bank 1 date, petition debt, fee + deposit (prefilled from Thresholds, editable), Bank 1 and Bank 5
 *  PDFs (required) plus any supporting documents → OneDrive / Court. The backend enforces gate G6 and creates "File petition at court". */
import { useState } from "react";
import { Loader2, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { recordPetitionPrepared } from "@/lib/api";
import { CourtField, Field, FileDrop, UploadProgress, errMsg, money, today, uploadAll } from "./petitionShared";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

export default function PetitionDetailsDialog({ caseRow, state, onClose, onSaved, onGate }) {
  const th = state?.thresholds || {};
  const [form, setForm] = useState({ court: state?.default_court || null, dated: today(), debt: state?.balance ? Number(state.balance).toFixed(2) : "", fee: th.fee_gbp ?? 332, deposit: th.deposit_gbp ?? 1500, notes: "" });
  const [bank1, setBank1] = useState([]);
  const [bank5, setBank5] = useState([]);
  const [extra, setExtra] = useState([]);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const valid = !!form.court && !!form.dated && Number(form.debt) > 0 && bank1.length === 1 && bank5.length === 1;

  const submit = async () => {
    setBusy(true);
    try {
      const files = await uploadAll([{ file: bank1[0], kind: "bank1" }, { file: bank5[0], kind: "bank5" }, ...extra.map((f) => ({ file: f, kind: "other" }))], setProgress);
      const r = await recordPetitionPrepared(caseRow.case_id, { ...form, debt: Number(form.debt), fee: Number(form.fee), deposit: Number(form.deposit), files });
      (r.warnings || []).forEach((w) => toast.warning(w));
      const n = (r.created_events || []).length;
      toast.success(`Petition details recorded · ${files.length} file${files.length === 1 ? "" : "s"} saved to OneDrive / Court${n ? ` · "File petition at court" diarised for ${r.created_events[0].owner || "the legal owner"}` : ""}`);
      onSaved?.(r);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.gate) { toast.error(errorDetailMessage(e, "")); onGate?.(d.gate.gate); }
      else toast.error(errMsg(e));
    } finally { setBusy(false); setProgress(null); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="petition-details-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2"><ScrollText className="w-4 h-4 text-violet-700" />Record petition details — {caseRow.customer_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <Field label="Court the petition is presented to" hint="Defaults to the guarantor's issuing court on the file">
            <CourtField court={form.court} onChange={set("court")} testId="petition-court" />
          </Field>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Bank 1 date"><Input type="date" value={form.dated} max={today()} onChange={set("dated")} className="h-8 text-xs" data-testid="petition-dated" /></Field>
            <Field label="Petition debt £" hint={state?.balance ? `balance ${money(state.balance)}` : undefined}><Input type="number" step="0.01" min="0" value={form.debt} onChange={set("debt")} className="h-8 text-xs" data-testid="petition-debt" /></Field>
            <Field label="Court fee £"><Input type="number" step="0.01" min="0" value={form.fee} onChange={set("fee")} className="h-8 text-xs" data-testid="petition-fee" /></Field>
            <Field label="OR deposit £"><Input type="number" step="0.01" min="0" value={form.deposit} onChange={set("deposit")} className="h-8 text-xs" data-testid="petition-deposit" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Bank 1 — creditor's bankruptcy petition (PDF)"><FileDrop files={bank1} setFiles={setBank1} busy={busy} multiple={false} required accept="application/pdf,.pdf" label="Attach Bank 1" hint="required" testId="petition-bank1" /></Field>
            <Field label="Bank 5 — statement of truth (PDF)"><FileDrop files={bank5} setFiles={setBank5} busy={busy} multiple={false} required accept="application/pdf,.pdf" label="Attach Bank 5" hint="required" testId="petition-bank5" /></Field>
          </div>
          <Field label="Supporting documents (optional)"><FileDrop files={extra} setFiles={setExtra} busy={busy} accept="application/pdf,.pdf" label="Add document" hint="exhibits, schedule of debt… (PDF)" testId="petition-extra" /></Field>
          <Field label="Note for the file (optional)"><Textarea rows={2} value={form.notes} onChange={set("notes")} className="text-xs" data-testid="petition-notes" /></Field>
          <UploadProgress progress={progress} testId="petition-upload-progress" />
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="petition-details-cancel">Cancel</Button>
          <Button size="sm" className="bg-violet-700 hover:bg-violet-800" onClick={submit} disabled={busy || !valid} data-testid="petition-details-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ScrollText className="w-3.5 h-3.5 mr-1" />}{busy ? (progress ? "Uploading…" : "Saving…") : "Record petition details"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
